// Themes suggested from the logo's own colours. Sampling happens in the
// browser; nothing is sent anywhere.
import { useCallback, useEffect, useState } from 'react'
import { contrastRatio } from './settingsModel'

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const MAX_COLORS = 3
const MIN_HUE_GAP = 25

export function rgbToHsl(r, g, b) {
  const rn = r / 255, gn = g / 255, bn = b / 255
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
  else if (max === gn) h = (bn - rn) / d + 2
  else h = (rn - gn) / d + 4
  return [h * 60, s, l]
}

export function hslToHex(h, s, l) {
  const k = n => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = n => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))))
  return `#${[f(0), f(8), f(4)].map(v => v.toString(16).padStart(2, '0')).join('')}`
}

export const hexToHsl = hex => {
  const n = parseInt(hex.slice(1), 16)
  return rgbToHsl((n >> 16) & 255, (n >> 8) & 255, n & 255)
}

const hueGap = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d }

// Up to three brand colours from RGBA pixel data, most common first, each a
// clearly different hue. Transparent, near-white, near-black and grey pixels
// are background, not brand.
export function dominantColors(data) {
  const buckets = new Map()
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2]
    if (data[i + 3] < 200) continue
    const [h, s, l] = rgbToHsl(r, g, b)
    if (l > 0.94 || l < 0.06 || s < 0.12) continue
    const key = `${Math.round(h / 12)}-${Math.round(s * 4)}-${Math.round(l * 4)}`
    const bucket = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 }
    bucket.count += 1
    bucket.r += r; bucket.g += g; bucket.b += b
    buckets.set(key, bucket)
  }

  const picked = []
  const ranked = [...buckets.values()].sort((a, b) => b.count - a.count)
  for (const bucket of ranked) {
    const r = Math.round(bucket.r / bucket.count), g = Math.round(bucket.g / bucket.count), b = Math.round(bucket.b / bucket.count)
    const [h] = rgbToHsl(r, g, b)
    if (picked.some(c => hueGap(c.h, h) < MIN_HUE_GAP)) continue
    picked.push({ h, hex: `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}` })
    if (picked.length === MAX_COLORS) break
  }
  return picked.map(c => c.hex)
}

const HUE_NAMES = [[15, 'red'], [40, 'orange'], [65, 'gold'], [160, 'green'], [200, 'teal'], [250, 'blue'], [290, 'violet'], [335, 'pink'], [361, 'red']]
export const hueName = hex => HUE_NAMES.find(([limit]) => hexToHsl(hex)[0] < limit)[1]

const hexHueGap = (a, b) => hueGap(hexToHsl(a)[0], hexToHsl(b)[0])
const isNeutralHex = hex => hexToHsl(hex)[1] < 0.22
const titleCase = word => word[0].toUpperCase() + word.slice(1)

// Status colours in order of preference. The first one whose hue is clearly
// apart from the brand colours wins, so a red logo never gets a red "error"
// that looks like its own buttons.
const STATUS_CHOICES = {
  success: ['#16a34a', '#059669', '#4d8f1f'],
  warning: ['#d97706', '#ca8a04', '#a16207'],
  error: ['#dc2626', '#e11d48', '#c2410c'],
  info: ['#2563eb', '#0284c7', '#0891b2', '#4f46e5'],
}
const STATUS_HUE_GAP = 30

export function statusColors(avoid) {
  const brand = avoid.filter(hex => !isNeutralHex(hex))
  const nearest = hex => Math.min(360, ...brand.map(a => hexHueGap(hex, a)))
  // When none is clear, the one furthest from the brand still reads best.
  const choose = options => options.find(hex => nearest(hex) >= STATUS_HUE_GAP)
    || options.reduce((best, hex) => (nearest(hex) > nearest(best) ? hex : best))
  return Object.fromEntries(Object.entries(STATUS_CHOICES).map(([key, options]) => [key, choose(options)]))
}

// Button colours for a logo colour. Keeps the exact colour whenever some text
// reads on it: white on darker colours, a deep ink of the same hue on light
// ones (turquoise, yellow). Only darkens when neither works.
export function buttonFor(hex) {
  const [h, s, l] = hexToHsl(hex)
  const shades = (base, text, light) => ({
    primary: base,
    primary_hover: hslToHex(h, s, clamp(light - 0.06, 0, 1)),
    primary_active: hslToHex(h, s, clamp(light - 0.12, 0, 1)),
    primary_text: text,
  })

  if (contrastRatio('#ffffff', hex) >= 4.5) return shades(hex, '#ffffff', l)

  const ink = hslToHex(h, clamp(s, 0.3, 0.6), 0.1)
  if (contrastRatio(ink, hslToHex(h, s, l - 0.06)) >= 4.5) return shades(hex, ink, l)

  const sat = clamp(s, 0.4, 0.8)
  let dark = l
  while (dark > 0.2 && contrastRatio('#ffffff', hslToHex(h, sat, dark)) < 4.5) dark -= 0.02
  return {
    primary: hslToHex(h, sat, dark),
    primary_hover: hslToHex(h, sat, dark - 0.06),
    primary_active: hslToHex(h, sat, dark - 0.12),
    primary_text: '#ffffff',
  }
}

// Sidebar shades: a deep version of the colour, or a charcoal with a hint of it.
const deepSidebar = hex => { const [h, s] = hexToHsl(hex); return hslToHex(h, clamp(s, 0.35, 0.7), 0.16) }
const slateSidebar = hex => { const [h] = hexToHsl(hex); return hslToHex(h, 0.18, 0.13) }

function build({ sidebar, button, avoid }, tokenDefaults) {
  const buttons = buttonFor(button)
  return {
    primary_color: sidebar,
    tokens: {
      ...tokenDefaults,
      ...buttons,
      ...statusColors(avoid),
      background: sidebar,
      toggle_on: buttons.primary,
    },
  }
}

// The bold style for one colour; kept for callers that want a single theme.
export const themeFromColor = (hex, tokenDefaults) => build({ sidebar: deepSidebar(hex), button: hex, avoid: [hex] }, tokenDefaults)

const MAX_SUGGESTIONS = 5

// Suggested themes, ready to use as tiles: for each of the two main colours a
// bold (coloured sidebar) and a minimal (neutral sidebar) style, then a two-colour theme when the logo has one.
export function logoThemes(colors, tokenDefaults) {
  const [a, b] = colors
  const specs = []
  for (const hex of [a, b].filter(Boolean)) {
    const hue = hueName(hex)
    specs.push({
      name: `Bold ${hue}`, caption: 'Logo colour, deep sidebar', style: 'bold',
      sidebar: deepSidebar(hex), button: hex, avoid: [hex], sources: [hex],
    })
    specs.push({
      name: `Minimal ${hue}`, caption: 'Logo colour, neutral sidebar', style: 'minimal',
      sidebar: slateSidebar(hex), button: hex, avoid: [hex], sources: [hex],
    })
  }
  if (a && b) {
    specs.splice(2, 0, {
      name: `${titleCase(hueName(a))} & ${hueName(b)}`, caption: 'Both logo colours', style: 'duo',
      sidebar: deepSidebar(a), button: b, avoid: [a, b], sources: [a, b],
    })
  }

  const seen = new Map()
  return specs.slice(0, MAX_SUGGESTIONS).map((spec, i) => {
    const n = (seen.get(spec.name) || 0) + 1
    seen.set(spec.name, n)
    return {
      id: `logo-${i}`,
      name: n > 1 ? `${spec.name} ${n}` : spec.name,
      caption: spec.caption,
      style: spec.style,
      sources: spec.sources,
      suggested: true,
      ...build(spec, tokenDefaults),
    }
  })
}

function sample(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const SIZE = 64
        const canvas = document.createElement('canvas')
        canvas.width = SIZE
        canvas.height = SIZE
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0, SIZE, SIZE)
        resolve(dominantColors(ctx.getImageData(0, 0, SIZE, SIZE).data))
      } catch (err) {
        reject(err) // tainted canvas: the image host doesn't allow reading pixels
      }
    }
    img.onerror = reject
    img.src = url
  })
}

// Colours already read, by logo URL. Kept in this browser because a logo in
// blob storage usually can't be re-read: the storage account sends no CORS
// headers, so the browser won't hand its pixels to the page. The colours are
// captured once (from the uploaded file, or a file the admin picks) and reused.
const STORE_KEY = 'xtract.settings.logoColours'
const MAX_REMEMBERED = 20
const listeners = new Set()

function loadStore() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}
const cache = new Map(Object.entries(loadStore()))

function remember(url, colors) {
  cache.delete(url)
  cache.set(url, colors)
  while (cache.size > MAX_REMEMBERED) cache.delete(cache.keys().next().value)
  try { window.localStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(cache))) } catch { /* storage blocked */ }
  listeners.forEach(fn => fn(url))
}

// Colours for the logo at `url`, read from `sampleUrl` when given (a local
// copy of the same image). `readFile` reads a picked file as that logo.
// status: 'idle' | 'loading' | 'ready' | 'error'
export function useLogoColors(url, sampleUrl) {
  const [state, setState] = useState({ status: 'idle', colors: [] })

  useEffect(() => {
    if (!url) { setState({ status: 'idle', colors: [] }); return undefined }
    if (cache.has(url)) { setState({ status: 'ready', colors: cache.get(url) }); return undefined }
    let live = true
    setState({ status: 'loading', colors: [] })
    sample(sampleUrl || url)
      .then(colors => { remember(url, colors) })
      .catch(() => { if (live) setState({ status: 'error', colors: [] }) })
    return () => { live = false }
  }, [url, sampleUrl])

  // Pick up colours read elsewhere on the page (the other tab, a picked file).
  useEffect(() => {
    const onChange = changed => { if (changed === url) setState({ status: 'ready', colors: cache.get(url) }) }
    listeners.add(onChange)
    return () => { listeners.delete(onChange) }
  }, [url])

  const readFile = useCallback(async file => {
    if (!url || !file) return false
    const local = URL.createObjectURL(file)
    setState({ status: 'loading', colors: [] })
    try {
      remember(url, await sample(local))
      return true
    } catch {
      setState({ status: 'error', colors: [] })
      return false
    } finally {
      URL.revokeObjectURL(local)
    }
  }, [url])

  return { ...state, readFile }
}
